import { DeliverableTab } from 'pages/licenses/combo_license/components/DeliverableTab';
import { NumericInput } from 'pages/licenses/combo_license/components/NumericInput';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { Book, Course, LicenseFormPayload, Product } from 'apis/LicenseApi';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { Nav, Tab } from 'react-bootstrap';

export type FormErrors = Partial<Record<keyof LicenseFormPayload, string>>;

export type ComboLicenseFormProps = {
    books: Book[];
    courses: Course[];
    errors: FormErrors;
    onChange: (next: LicenseFormPayload) => void;
    products: Product[];
    value: LicenseFormPayload;
};

export const ComboLicenseForm = (props: ComboLicenseFormProps) => {

    const { formatMessage } = useIntl();

    const { books, courses, errors, onChange, products, value } = props;

    const set = <K extends keyof LicenseFormPayload>(key: K, val: LicenseFormPayload[K]) => onChange({ ...value, [key]: val });

    const isSeat = value.licenseType === 3;

    return (
        <Tab.Container defaultActiveKey="settings">
            <Nav variant="tabs">
                <Nav.Item>
                    <Nav.Link eventKey="settings">
                        <FormattedMessage defaultMessage="License Settings" id="license_settings.2cab17ba" />
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="practice-tests">
                        <FormattedMessage defaultMessage="Practice Tests" id="practice_tests.f40d347b" />
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="courses">
                        <FormattedMessage defaultMessage="Courses" id="courses.c269a3fa" />
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="ebooks">
                        eBooks
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            <Tab.Content className="border border-top-0 p-3 bg-white">

                {/* ── License Settings ────────────────────────────────── */}
                <Tab.Pane eventKey="settings">
                    <h5 className="fw-bold mt-2 mb-3">
                        <FormattedMessage defaultMessage="General Settings" id="general_settings.23e46a2e" />
                    </h5>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <GMetrixInput
                                required
                                feedbackMessage={errors.name}
                                label={formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })}
                                onChange={e => set('name', (e.target as HTMLInputElement).value)}
                                validType={errors.name ? 'invalid' : undefined}
                                value={value.name}
                            />
                        </div>
                        <div className="col-md-4">
                            <GMetrixInput
                                feedbackMessage={errors.prefix}
                                label={formatMessage({ id: 'prefix.8e213121', defaultMessage: 'Prefix' })}
                                maxLength={20}
                                onChange={e => set('prefix', (e.target as HTMLInputElement).value)}
                                validType={errors.prefix ? 'invalid' : undefined}
                                value={value.prefix}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <GMetrixInput
                                required
                                feedbackMessage={errors.licenseDuration}
                                label={formatMessage({ id: 'duration_days.c2b0a7a1', defaultMessage: 'Duration (days)' })}
                                min={1}
                                onChange={e => set('licenseDuration', Number((e.target as HTMLInputElement).value))}
                                type="number"
                                validType={errors.licenseDuration ? 'invalid' : undefined}
                                value={value.licenseDuration === 0 ? '' : value.licenseDuration}
                            />
                        </div>
                        <div className="col-md-4">
                            <GMetrixInput
                                required
                                feedbackMessage={errors.usageDays}
                                label={formatMessage({ id: 'student_usage_days.8790862b', defaultMessage: 'Student usage days' })}
                                min={1}
                                onChange={e => set('usageDays', Number((e.target as HTMLInputElement).value))}
                                type="number"
                                validType={errors.usageDays ? 'invalid' : undefined}
                                value={value.usageDays === 0 ? '' : value.usageDays}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <h6 className="fw-bold">
                                <FormattedMessage defaultMessage="License Type" id="license_type.e4d2a715" />
                            </h6>
                            {[{ label: formatMessage({ id: 'site.84ff8857', defaultMessage: 'Site' }), val: 1 },
                                { label: formatMessage({ id: 'individual.03bcdacf', defaultMessage: 'Individual' }), val: 2 },
                                { label: formatMessage({ id: 'seat.42016b6c', defaultMessage: 'Seat' }), val: 3 }].map(({ label, val }) => (
                                    <GMetrixCheckInput
                                        checked={value.licenseType === val}
                                        id={`licType-${val}`}
                                        key={val}
                                        label={label}
                                        name="licenseType"
                                        onChange={() => set('licenseType', val)}
                                        type="radio"
                                    />
                            ))}
                        </div>
                        <div className="col-md-5">
                            <h6 className="fw-bold">
                                <FormattedMessage defaultMessage="Associate on first use" id="associate_on_first_use.82b403c0" />
                            </h6>
                            {[{ label: formatMessage({ id: 'dont_associate_on_first_use.b4b3b38d', defaultMessage: 'Don\'t associate on first use' }), val: 0 },
                                { label: formatMessage({ id: 'allow_administrator_to_set_association.1926afaf', defaultMessage: 'Allow administrator to set association' }), val: 1 },
                                { label: formatMessage({ id: 'force_association_on_first_use.c449646f', defaultMessage: 'Force association on first use' }), val: 2 }].map(({ label, val }) => (
                                    <GMetrixCheckInput
                                        checked={value.allowAssociate === val}
                                        id={`assoc-${val}`}
                                        key={val}
                                        label={label}
                                        name="allowAssociate"
                                        onChange={() => set('allowAssociate', val)}
                                        type="radio"
                                    />
                            ))}
                        </div>
                    </div>

                    <div className="row mb-3">
                        {isSeat && (
                            <>
                                <div className="col-md-3">
                                    <NumericInput
                                        onChange={v => {

                                            // Mirror seat counts across all deliverables
                                            onChange({
                                                ...value,
                                                books: { ...value.books, seats: v },
                                                courses: { ...value.courses, seats: v },
                                                practiceTests: { ...value.practiceTests, seats: v }
                                            });

                                        }}
                                        label={formatMessage({ id: 'seats.e0bcdf2b', defaultMessage: 'Seats' })}
                                        value={value.practiceTests.seats}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <NumericInput
                                        onChange={v => {

                                            onChange({
                                                ...value,
                                                books: { ...value.books, maxSeatsPerUser: v },
                                                courses: { ...value.courses, maxSeatsPerUser: v },
                                                practiceTests: { ...value.practiceTests, maxSeatsPerUser: v }
                                            });

                                        }}
                                        label={formatMessage({ id: 'max_users_per_seat.4f116e74', defaultMessage: 'Max users per seat' })}
                                        value={value.practiceTests.maxSeatsPerUser}
                                    />
                                </div>
                            </>
                        )}
                        <div className="col-md-3 d-flex align-items-end">
                            <GMetrixCheckInput
                                checked={value.allowOffline}
                                id="allowOffline"
                                label={formatMessage({ id: 'allow_offline.4492445e', defaultMessage: 'Allow offline' })}
                                onChange={e => set('allowOffline', e.target.checked)}
                                type="checkbox"
                            />
                        </div>
                    </div>
                </Tab.Pane>

                {/* ── Practice Tests ──────────────────────────────────── */}
                <Tab.Pane mountOnEnter unmountOnExit eventKey="practice-tests">
                    <DeliverableTab
                        contentLabel="Practice test"
                        deliverable={value.practiceTests}
                        getGroupKey={(item: Product) => item.Category?.Name ?? ''}
                        items={products}
                        onChange={next => set('practiceTests', next)}
                        showSeats={isSeat}
                    />
                </Tab.Pane>

                {/* ── Courses ─────────────────────────────────────────── */}
                <Tab.Pane mountOnEnter unmountOnExit eventKey="courses">
                    <DeliverableTab
                        includeOtherBucket
                        contentLabel="Course"
                        deliverable={value.courses}
                        getGroupKey={(item: Course) => (item.Category?.Id === 0 ? '' : item.Category?.Name ?? '')}
                        items={courses}
                        onChange={next => set('courses', next)}
                        showSeats={isSeat}
                    />
                </Tab.Pane>

                {/* ── eBooks ──────────────────────────────────────────── */}
                <Tab.Pane mountOnEnter unmountOnExit eventKey="ebooks">
                    <DeliverableTab
                        contentLabel="eBook"
                        deliverable={value.books}
                        getGroupKey={(item: Book) => item.Category?.Name ?? ''}
                        items={books}
                        onChange={next => set('books', next)}
                        showSeats={isSeat}
                    />
                </Tab.Pane>

            </Tab.Content>
        </Tab.Container>
    );

};
